// Transient confirmations and errors.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Icon } from './Icon';

interface Toast {
  id: number;
  text: string;
  tone: 'success' | 'error';
}

interface ToastValue {
  success: (text: string) => void;
  error: (text: string) => void;
}

const ToastContext = createContext<ToastValue | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((text: string, tone: Toast['tone']) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, text, tone }]);
    // Errors linger longer; they usually need reading rather than glancing at.
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)),
      tone === 'error' ? 7000 : 3500);
  }, []);

  const value = useMemo<ToastValue>(() => ({
    success: (text) => push(text, 'success'),
    error: (text) => push(text, 'error'),
  }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`} role="status">
            <Icon name={toast.tone === 'success' ? 'check-circle' : 'alert-circle'} />
            <div className="toast__text">{toast.text}</div>
            <button
              type="button"
              className="btn btn--ghost btn--sm btn--icon"
              onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
              aria-label="Close"
            >
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
